// Fallback message for when the estimate is too high
const TIME_UNIT_OUTSIDE_RANGE_MESSAGE =
    "This estate appears highly complex, and the administration fees would be calculated at an hourly rate. Please contact QPT on [1300 360 044](tel:1300360044) for more information.";
/**
 * Convert time units into an estimated cost range.
 *
 * @param {Integer} timeUnits The number of time units
 *
 * @returns String
 */
function timeUnitToRange(timeUnits) {
    let rangeEstimate = "";

    switch (true) {
        case timeUnits > 0 && timeUnits < 21:
            rangeEstimate = "$477.50";
            break;

        case timeUnits > 20 && timeUnits < 31:
            rangeEstimate = "$664.35 to $1,078.85";
            break;

        case timeUnits > 30 && timeUnits < 41:
            rangeEstimate = "$1,078.85 to $1,369.50";
            break;

        case timeUnits > 40 && timeUnits < 51:
            rangeEstimate = "$1,369.50 to $1,621.15";
            break;

        case timeUnits > 50 && timeUnits < 76:
            rangeEstimate = "$1,621.15 to $1,810.80";
            break;

        case timeUnits > 75 && timeUnits < 91:
            rangeEstimate = "$1,810.80 to $2,472.80";
            break;

        case timeUnits > 90 && timeUnits < 111:
            rangeEstimate = "$2,472.80 to $3,566.70";
            break;

        case timeUnits > 110 && timeUnits < 131:
            rangeEstimate = "$3,566.70 to $4,112.95";
            break;

        case timeUnits > 130 && timeUnits < 151:
            rangeEstimate = "$4,112.95 to $4,660.65";
            break;

        case timeUnits > 150 && timeUnits < 171:
            rangeEstimate = "$4,660.65 to $5,216.35";
            break;

        case timeUnits > 170 && timeUnits < 191:
            rangeEstimate = "$5,216.35 to $6,170.35";
            break;

        case timeUnits > 190 && timeUnits < 226:
            rangeEstimate = "$6,170.35 to $7,269.65";
            break;

        case timeUnits > 225 && timeUnits < 266:
            rangeEstimate = "$7,269.65 to $8,366.20";
            break;

        case timeUnits > 265 && timeUnits < 306:
            rangeEstimate = "$8,366.20 to $9,462.20";
            break;

        case timeUnits > 305 && timeUnits < 346:
            rangeEstimate = "$9,462.20 to $10,555.40";
            break;

        case timeUnits > 345 && timeUnits < 386:
            rangeEstimate = "$10,555.40 to $12,344.95";
            break;

        case timeUnits > 385 && timeUnits < 451:
            rangeEstimate = "$12,344.95 to $14,131.85";
            break;

        case timeUnits > 450 && timeUnits < 516:
            rangeEstimate = "$14,131.85 to $16,874.05";
            break;

        case timeUnits > 515 && timeUnits < 616:
            rangeEstimate = "$16,874.05 to $19,614.80";
            break;

        case timeUnits > 615 && timeUnits < 716:
            rangeEstimate = "$19,614.80 to $22,357.15";
            break;

        case timeUnits > 715 && timeUnits < 816:
            rangeEstimate = "$22,357.15 to $26,459.60";
            break;

        case timeUnits > 815 && timeUnits < 966:
            rangeEstimate = "$26,459.60 to $30,581.05";
            break;

        case timeUnits > 965 && timeUnits < 1116:
            rangeEstimate = "$30,581.05 to $36,065.40";
            break;

        case timeUnits > 1115 && timeUnits < 1316:
            rangeEstimate = "$36,065.40";
            break;

        default:
            break;
    }

    return rangeEstimate;
}

/**
 * Use the form responses to generate a cost estimate.
 * First, calculate the total time units required and
 * then convert that into an estimate - or estimated range.
 *
 * @param {Array} formResponses All of the user's responses to the calculator questions
 * @param {Object} calculationValues Additional calculation values. Specifically, key/value pairs of the time unit values for each input
 *
 * @returns Object
 */
export const generateEstimate = (formResponses, calculationValues) => {
    const estimateResults = {
        estimatedRange: "",
        totalTimeUnits: 0,
        timeUnitsWithinRange: true,
    };
    const allTimeUnits = [];
    const activityTimeUnitValues = {
        ...calculationValues.activityTimeUnitValues,
    };
    let summedTimeUnits = 0;
    let responseNumberParsed = 0;
    let responseFieldName = "";
    let responseTimeUnits = "";
    let estimatedRange = "";

    // Add a base minimum for "Executor Duties"
    allTimeUnits.push(75);

    // Loop over all the form responses
    for (let i = 0, j = formResponses.length; i < j; ++i) {
        // Capture the response value as an integer
        responseNumberParsed = parseInt(formResponses[i].fieldValue, 10);

        // Capture the response name
        responseFieldName = formResponses[i].fieldName;

        /**
         * Look up the corresponding time unit.
         * The key name should map to the form input name.
         * This makes the lookup very quick and easy to map.
         */
        responseTimeUnits = activityTimeUnitValues[responseFieldName];

        // Check if corresponding time units were found
        if (responseTimeUnits) {
            // Check if the response is an integer
            if (!isNaN(responseNumberParsed)) {
                // Special rules are checked first
                if (responseFieldName === "beneficiaries-australia") {
                    // The first 3 solely owned Australian beneficiaries are included in the minimum
                    if (responseNumberParsed > 3) {
                        // Add the time units to the total
                        allTimeUnits.push(
                            responseTimeUnits * (responseNumberParsed - 3),
                        );
                    }
                    continue;
                }

                if (
                    responseFieldName ===
                    "liabilities-unsecured-australia-solely"
                ) {
                    // The first 5 solely owned Australian unsecured liabilities are included in the minimum
                    if (responseNumberParsed > 5) {
                        // Add the time units to the total
                        allTimeUnits.push(
                            responseTimeUnits * (responseNumberParsed - 5),
                        );
                    }
                    continue;
                }

                // Default.
                // Add the time units to the total
                allTimeUnits.push(responseTimeUnits * responseNumberParsed);
            }

            // Check if the response was a "boolean" (i.e. Yes/No)
            if (formResponses[i].fieldValue === "Yes") {
                // Add the time units to the total
                allTimeUnits.push(responseTimeUnits);
            }
        }

        // Other specific rules without a value to lookup
        if (responseFieldName === "other-estimated-estate-value") {
            switch (formResponses[i].fieldValue) {
                case "$75,001 to $150,000":
                    // Add set time units to the total
                    allTimeUnits.push(50);
                    break;

                case "Over $150,000":
                    // Add set time units to the total
                    allTimeUnits.push(75);
                    break;

                default:
                    break;
            }
        }
    }

    // Sum all the captured time units
    summedTimeUnits = allTimeUnits.reduce(
        (accumulator, currentValue) => accumulator + currentValue,
    );

    // Get the corresponding range
    estimatedRange = timeUnitToRange(summedTimeUnits);

    // Capture the total time units
    estimateResults.totalTimeUnits = summedTimeUnits;

    if (estimatedRange === "") {
        // Estimate is outside the range
        estimateResults.estimatedRange = TIME_UNIT_OUTSIDE_RANGE_MESSAGE;
        estimateResults.timeUnitsWithinRange = false;
    } else {
        // Estimate is within the range
        estimateResults.estimatedRange = estimatedRange;
    }

    return estimateResults;
};
